import { useEffect, useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { Tree, type TreeExpandedKeysType } from 'primereact/tree';
import { toast } from 'sonner';
import type { ChatReplyFolderResponse, ChatReplyTemplateResponse } from '@/core/generated/api';
import { chatReplyTemplateService } from '@/entities/ChatReplyTemplate/api/chatReplyTemplateService';
import { useChatReplyTemplates } from '@/entities/ChatReplyTemplate/model/store';
import { ChatReplyFolderEditor } from '@/features/ChatReplyTemplates/ChatReplyFolderEditor';
import { ChatReplyTemplateEditor } from '@/features/ChatReplyTemplates/ChatReplyTemplateEditor';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import {
    RequestEmpty,
    RequestError,
    RequestErrorBanner,
    RequestLoading,
} from '@/shared/ui/RequestState';
import classes from './ChatReplyTemplatesPage.module.scss';

/** Управляет папками, подпапками и шаблонами сообщений на одной странице */
export const ChatReplyTemplatesPage = () => {
    const catalog = useChatReplyTemplates(state => state.catalog);
    const status = useChatReplyTemplates(state => state.status);
    const error = useChatReplyTemplates(state => state.error);
    const load = useChatReplyTemplates(state => state.load);
    const [expandedKeys, setExpandedKeys] = useState<TreeExpandedKeysType>({});
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [folderEditor, setFolderEditor] = useState<{ folder?: ChatReplyFolderResponse; parentId?: string | null } | null>(null);
    const [templateEditor, setTemplateEditor] = useState<{ template?: ChatReplyTemplateResponse } | null>(null);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const canCreate = useCanAccessPermission('admin.chat-replies.create');
    const canEdit = useCanAccessPermission('admin.chat-replies.edit');
    const canDelete = useCanAccessPermission('admin.chat-replies.delete');
    const folders = catalog?.folders ?? [];
    const templates = catalog?.templates ?? [];
    const selected = folders.find(folder => folder.id === selectedId) ?? folders.find(folder => !folder.parentId);
    const messages = templates.filter(template => template.folderId === selected?.id);
    const loading = busy || status === RequestStatus.Loading;

    useEffect(() => {
        void load(true);
    }, [load]);

    useEffect(() => {
        const parentId = selected?.parentId;

        if (parentId) setExpandedKeys(keys => ({
            ...keys,
            [parentId]: true
        }));
    }, [selected?.parentId]);

    /** Удаляет запись и обновляет каталог */
    const remove = (
        item: ChatReplyFolderResponse | ChatReplyTemplateResponse,
        isFolder: boolean
    ): void => {
        if (!canDelete || !item.id || !item.version || loading) return;

        const id = item.id;
        const version = item.version;

        confirmDialog({
            header: isFolder
                ? 'Удалить папку?'
                : 'Удалить шаблон?',
            message: `Удалить «${item.name}»?`,
            acceptLabel: 'Удалить',
            rejectLabel: 'Отмена',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setBusy(true);
                setActionError(null);

                try {
                    if (isFolder) await chatReplyTemplateService.deleteFolder(id, version);
                    else await chatReplyTemplateService.deleteTemplate(id, version);

                    toast.success(isFolder
                        ? 'Папка удалена'
                        : 'Шаблон удалён');

                    await load(true);
                } catch (failure: unknown) {
                    setActionError(getErrorMessage(failure, 'Не удалось удалить запись'));
                } finally {
                    setBusy(false);
                }
            },
        });
    };

    if (!catalog && (status === RequestStatus.Idle || status === RequestStatus.Loading)) {
        return <RequestLoading message="Загрузка шаблонов сообщений..." />;
    }

    const tree = folders.filter(folder => !folder.parentId)
        .map(folder => ({
            key: folder.id,
            label: folder.name ?? '',
            icon: 'pi pi-folder',
            children: folders.filter(child => child.parentId === folder.id)
                .map(child => ({
                    key: child.id,
                    label: child.name ?? '',
                    icon: 'pi pi-folder',
                })),
        }));

    return <div className={classes.page}>
        <h1>Шаблоны сообщений чата</h1>
        <p>Создавайте папки и готовые ответы. В чате нажмите правой кнопкой на поле сообщения и выберите шаблон.</p>
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить шаблоны"
            onRetry={() => load(true)}
        />
        {actionError &&
            <RequestError
                message={actionError}
                compact
            />
        }
        <div className={classes.workspace}>
            <section
                className={classes.panel}
                aria-label="Папки шаблонов"
            >
                <div className={classes.heading}>
                    <h2>Папки</h2>
                    <ActionButton
                        type="button"
                        label="Новая папка"
                        icon="pi pi-plus"
                        permission="admin.chat-replies.create"
                        disabled={loading || !canCreate}
                        onClick={() => setFolderEditor({ parentId: null })}
                    />
                </div>
                {folders.length === 0
                    ? <RequestEmpty message="Создайте первую папку для готовых ответов" />
                    : <Tree
                        value={tree}
                        expandedKeys={expandedKeys}
                        onToggle={event => setExpandedKeys(event.value)}
                        pt={{ toggler: { 'aria-label': 'Развернуть или свернуть папку' } }}
                        selectionMode="single"
                        selectionKeys={selected?.id ?? null}
                        onSelectionChange={event => {
                            if (typeof event.value === 'string') setSelectedId(event.value);
                        }}
                        aria-label="Папки шаблонов"
                    />}
                {selected &&
                    <div className={classes.folderActions}>
                        <ActionButton
                            type="button"
                            label="Подпапка"
                            icon="pi pi-folder"
                            permission="admin.chat-replies.create"
                            disabled={loading || Boolean(selected.parentId)}
                            onClick={() => setFolderEditor({ parentId: selected.id })}
                        />
                        <ActionButton
                            type="button"
                            label="Изменить папку"
                            permission="admin.chat-replies.edit"
                            disabled={loading || !canEdit}
                            onClick={() => setFolderEditor({ folder: selected })}
                        />
                        <ActionButton
                            type="button"
                            label="Удалить папку"
                            color="danger"
                            permission="admin.chat-replies.delete"
                            disabled={loading}
                            onClick={() => remove(selected, true)}
                        />
                    </div>
                }
            </section>
            <section
                className={classes.panel}
                aria-label="Шаблоны выбранной папки"
            >
                <div className={classes.heading}>
                    <h2>{selected?.name ?? 'Шаблоны'}</h2>
                    <ActionButton
                        type="button"
                        label="Новый шаблон"
                        icon="pi pi-plus"
                        permission="admin.chat-replies.create"
                        disabled={loading || !selected}
                        onClick={() => setTemplateEditor({})}
                    />
                </div>
                {messages.length === 0 &&
                    <RequestEmpty message={selected
                        ? 'В этой папке пока нет шаблонов'
                        : 'Выберите или создайте папку'
                    } />
                }
                <div className={classes.messages}>
                    {messages.map(template => <article
                        key={template.id}
                        className={classes.message}
                    >
                        <h3>{template.name}</h3>
                        <p className={classes.text}>{template.text}</p>
                        <div className={classes.folderActions}>
                            <ActionButton
                                type="button"
                                label="Изменить"
                                permission="admin.chat-replies.edit"
                                disabled={loading}
                                onClick={() => setTemplateEditor({ template })}
                            />
                            <ActionButton
                                type="button"
                                label="Удалить"
                                color="danger"
                                permission="admin.chat-replies.delete"
                                disabled={loading}
                                onClick={() => remove(template, false)}
                            />
                        </div>
                    </article>)}
                </div>
            </section>
        </div>
        {folderEditor &&
            <ChatReplyFolderEditor
                {...folderEditor}
                folders={folders}
                onClose={() => setFolderEditor(null)}
                onSaved={id => {
                    setFolderEditor(null);
                    setSelectedId(id);
                    void load(true);
                }}
            />
        }
        {templateEditor &&
            <ChatReplyTemplateEditor
                {...templateEditor}
                folders={folders}
                folderId={selected?.id ?? ''}
                onClose={() => setTemplateEditor(null)}
                onSaved={() => {
                    setTemplateEditor(null);
                    void load(true);
                }}
            />
        }
    </div>;
};
